import { computed, DestroyRef, inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService as TranslateLibService } from '@ngx-translate/core';
import { AoTranslationsService } from '@shopware-operations/aorta';
import { isString } from '@shopware-operations/ts-utils';
import { filter, Observable } from 'rxjs';
import { CrossTabBroadcastService, CrossTabMessage } from '../cross-tab';

/** Message type for language change events */
const LANGUAGE_CHANGED_TYPE = 'i18n:language_changed';

/** Language change message with required payload */
interface LanguageChangedMessage {
  readonly type: typeof LANGUAGE_CHANGED_TYPE;
  readonly timestamp: number;
  readonly payload: { readonly language: string };
}

/** Type guard for language change messages */
const isLanguageChangedMessage = (msg: CrossTabMessage): msg is LanguageChangedMessage =>
  msg.type === LANGUAGE_CHANGED_TYPE &&
  typeof (msg.payload as { language?: string })?.language === 'string';

/**
 * Application translation service.
 *
 * Wraps ngx-translate with signal-based reactive translations and coordinates
 * with Aorta design system language settings.
 *
 * Supported languages: English (en), German (de)
 * Default language: English
 *
 * Provides three ways to access translations:
 * - Signal-based (reactive, auto-updates on language change)
 * - Observable-based (reactive streams)
 * - Instant (synchronous, current language)
 */
@Injectable({
  providedIn: 'root'
})
export class TranslateService {

  readonly #defaultLanguage: string = 'en';
  readonly #supportedLanguages: string[] = ['en', 'de'];

  readonly #translateLibService = inject(TranslateLibService);
  readonly #aoTranslationsService = inject(AoTranslationsService);
  readonly #crossTab = inject(CrossTabBroadcastService);
  readonly #destroyRef = inject(DestroyRef);

  constructor() {
    this.#initCrossTabSync();
  }

  /**
   * Initializes cross-tab synchronization for language changes.
   *
   * @remarks
   * Subscribes to language change messages from other tabs and applies
   * the new language directly (not via changeLanguage) to prevent re-broadcasting.
   */
  #initCrossTabSync(): void {
    this.#crossTab.messages$
      .pipe(
        filter(isLanguageChangedMessage),
        filter(msg => this.#supportedLanguages.includes(msg.payload.language)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(msg => {
        this.#setLanguage(msg.payload.language);
      });
  }

  // @ngx-translate/core v18 exposes `currentLang` as `Signal<Language | null>`,
  // already reactive on language change — no need to bridge `onLangChange`
  // through `toSignal`. Coerce `null` to the default language for a non-null
  // `Signal<string>` consumed across the app.
  readonly currentLanguage = computed(
    () => this.#translateLibService.currentLang() ?? this.#defaultLanguage
  );

  /**
   * Initializes translation service with language detection.
   *
   * @remarks
   * Language selection priority:
   * 1. URL parameter (if provided and valid)
   * 2. Browser language (if supported)
   * 3. Default language (en)
   *
   * Invalid language codes are silently ignored to maintain seamless UX in SSO flows.
   * Once set, language persists across SPA navigation via ngx-translate.
   *
   * @param langOverride - Optional language code from URL parameter (e.g., 'en', 'de')
   */
  initialize(langOverride?: string): void {
    // Default/fallback language is configured via `provideTranslateService`
    // in app.config (v18 replaced `setDefaultLang` with config-time options).

    // Priority 1: URL parameter override
    if (langOverride && this.#supportedLanguages.includes(langOverride)) {
      this.#setLanguage(langOverride);
      return;
    }

    // Priority 2: Browser language
    const clientLanguage = this.#translateLibService.getBrowserLang();
    if (isString(clientLanguage) && this.#supportedLanguages.includes(clientLanguage)) {
      this.#setLanguage(clientLanguage);
      return;
    }

    // Priority 3: Default
    this.#setLanguage(this.#defaultLanguage);
  }

  translateAsSignal = (key: string, params?: object): Signal<string> => (
    computed(() => {
      // call currentLang to make sure the signal is updated when the language changes
      this.currentLanguage();
      return this.#translateLibService.instant(key, params);
    })
  )

  translateObservable(key: string, params?: object): Observable<string> {
    return this.#translateLibService.stream(key, params);
  }

  translate(key: string, params?: object): string {
    return this.translateAsSignal(key, params)();
  }

  /**
   * Changes the UI language and broadcasts to other tabs.
   *
   * @param language - Language code to switch to (e.g., 'en', 'de')
   */
  changeLanguage(language: string): void {
    if (this.#supportedLanguages.includes(language)) {
      this.#setLanguage(language);
      // Notify other tabs of language change
      this.#crossTab.broadcast({
        type: 'i18n:language_changed',
        timestamp: Date.now(),
        payload: { language }
      });
    }
  }

  #setLanguage(language: string): void {
    this.#translateLibService.use(language);
    this.#aoTranslationsService.setLanguage(language);
  }
}
